/**
 * Decoração da Sala do Comitê — o que o template renderiza, já pronto.
 *
 * Mora fora do `.vue` por duas razões: o componente não chama função no
 * template (cada re-render reexecutaria a decoração inteira), e o arquivo
 * passaria de 500 linhas com a sala ao vivo dentro dele. Como efeito, cada
 * regra aqui é testável sem montar componente nenhum.
 *
 * A decoração é a MESMA para a sala ao vivo e para a reaberta: as duas chegam
 * no shape da transcrição (`reduzirEventos` garante isso), e é o que faz o
 * gestor ver a mesma coisa nos dois caminhos.
 */

import { toMoney } from '@utils/Format'
import { ICONE_ORQUESTRADOR, iconeDaArea } from '@modules/aawz-intelligence-v2/constants/Areas'
import {
  ESTADO_CONCLUIDO,
  ESTADO_INDISPONIVEL,
  MODO_REVISAO_DE_ANALISE,
  falaDe,
  falandoAgora,
  truncarQuote,
} from '@modules/aawz-intelligence-v2/utils/painel-deliberacao'

// Cor por classe de contribuição: carrega significado, não decoração. Uma
// restrição bloqueante não pode ter o mesmo peso visual de um alerta — é a
// diferença entre "a norma veda" e "considere isto".
export const COR_POR_CLASSE = {
  restricao: 'danger',
  dependencia: 'info',
  alerta: 'warning-new',
  alerta_obrigatorio: 'warning-new',
  proposta: 'secondary',
}
export const COR_CLASSE_PADRAO = 'secondary'

export const ROTULO_DA_CLASSE = {
  restricao: 'veda',
  dependencia: 'ordena',
  alerta: 'alerta',
  alerta_obrigatorio: 'alerta obrigatório',
  proposta: 'propõe',
}

// A posição da réplica é o desfecho do conflito — quem cedeu mudou o resultado.
// `nao_alcanca` também resolve: as duas áreas concordam que os escopos não se
// cruzam. Só `mantenho` deixa a divergência de pé.
export const COR_POR_POSICAO = {
  cedo: 'success',
  nao_alcanca: 'success',
  refino: 'info',
  mantenho: 'warning-new',
}
export const COR_POSICAO_PADRAO = 'secondary'

// A TAG discreta que substituiu o badge preenchido de `conduz`/`cede` [owner,
// 19/09/2026]: *"'conduz'/'cede' viraram tags discretas"*. A cor continua
// SIGNIFICANDO (quem cede muda o resultado; quem mantém deixa a divergência de
// pé) — ela só deixou de ser um retângulo cheio com ponto. O rótulo segue em
// texto, então nada aqui é transmitido só por cor (WCAG AA).
//
// A classe vem pronta daqui, e não de uma concatenação no `<template>`: regra
// do módulo — template não calcula.
export const CLASSE_POR_COR = {
  success: 'painel-tag--sucesso',
  info: 'painel-tag--info',
  'warning-new': 'painel-tag--atencao',
  danger: 'painel-tag--risco',
}
export const CLASSE_DE_TOM_PADRAO = ''

export const TITULO_DA_RODADA = {
  1: 'Rodada 1: cada área lê o caso sozinha',
  2: 'Rodada 2: as áreas respondem umas às outras',
  3: 'Rodada 3: sob que condição a restrição cai',
}

// No fluxo de revisão a rodada 1 não é parecer sobre pergunta: é crítica à
// tese que o gestor submeteu (US-7).
export const TITULO_DA_RODADA_1_REVISAO = 'Rodada 1: cada área critica a sua análise'

export const TEXTO_DO_ENCERRAMENTO = {
  mapa_vazio: 'As áreas não se contradisseram, não houve o que replicar.',
  replica: 'As divergências foram para réplica.',
  impasse:
    'Duas restrições ancoradas se excluem e nenhuma admite dispensa. O painel não resolveu.',
  degradado: 'Nenhuma área respondeu a tempo. A resposta saiu sem deliberação.',
}

// A natureza da divergência dita o que fazer com ela: fato se resolve com
// dado, premissa com decisão, apetite a risco é do gestor.
export const ROTULO_DA_NATUREZA = {
  fato: 'fato',
  premissa: 'premissa',
  apetite_a_risco: 'apetite a risco',
}

// Enquanto a área não devolve, a mesa está rodando — `aguardando` (entre a
// abertura e o primeiro status) e `analisando` leem igual para o gestor (W1).
export const TEXTO_ANALISANDO = 'analisando…'
export const TEXTO_NAO_RESPONDEU = 'não respondeu'

// O fecho da fala de uma área. É o ÚNICO anúncio que o card faz enquanto o
// texto cresce: o corpo da fala muda a cada token e, se ele fosse região viva,
// o leitor de tela releria o parágrafo inteiro a cada delta.
export const TEXTO_ANALISE_CONCLUIDA = 'Análise concluída'
export const ENDERECAMENTO_REVISAO = 'sobre a sua análise'

export const ROTULO_EXPANDIR = 'ver análise completa'
export const ROTULO_RECOLHER = 'ocultar análise completa'

// Quem abre a mesa. Mesma palavra no resumo do cabeçalho e na tag do turno —
// duas strings soltas divergiriam no primeiro ajuste de copy.
export const ROTULO_CONDUZ = 'conduz'

// O cabeçalho do cartão, no vocabulário de SEÇÃO (`ui-registry/cartao-casca`):
// sobretítulo em caixa alta pela classe, título em serif. A caixa alta NUNCA
// vem da string — quem escreve a copy escreve em caixa de sentença.
export const SOBRETITULO_DA_SALA = 'Consultor · Sala do Comitê'
export const TITULO_DA_SALA = 'Deliberação do painel'

// O rótulo da região das rodadas. Ele muda conforme exista OU NÃO um parecer
// acima: sem conclusão, "como o painel chegou aqui" apontaria para o nada.
export const TITULO_DAS_RODADAS = 'Como o painel chegou aqui'
export const TITULO_DAS_RODADAS_SEM_PARECER = 'A deliberação'

// O Orquestrador Sênior fecha a sala. Não é a oitava área: não tem domínio, não
// senta à mesa e não emite parecer de especialidade — arbitra o que as áreas
// disseram (`core/painel/papeis.py`). Daí o rótulo de PAPEL e o destaque visual
// próprio, em vez de mais um card de área.
export const ROTULO_ORQUESTRADOR = 'Orquestrador Sênior'
export const TITULO_DA_CONSOLIDACAO = 'Consolidação: o parecer do Orquestrador Sênior'
export const ROTULO_EXPANDIR_FUNDAMENTOS = 'ver fundamentos'
export const ROTULO_RECOLHER_FUNDAMENTOS = 'ocultar fundamentos'

// Quando a consolidação não pôde ser usada (fonte inventada, JSON inválido duas
// vezes, ou nenhuma área com parecer válido), o backend devolve a derivação
// determinística marcada com `consolidacao_fallback`. O gestor precisa saber que
// está lendo o parecer mais pobre — sem isso, um texto sem arbitragem passaria
// por arbitragem.
export const TEXTO_CONSOLIDACAO_AUTOMATICA = 'consolidação automática'
export const EXPLICACAO_CONSOLIDACAO_AUTOMATICA =
  'O parecer abaixo foi montado automaticamente a partir do que as áreas disseram: ' +
  'nesta deliberação o Orquestrador Sênior não pôde arbitrar.'

function valorDoMapa(mapa, chave, padrao) {
  if (chave && Object.hasOwn(mapa, chave)) return mapa[chave]
  return padrao
}

/**
 * O título da rodada, com a variante do fluxo de revisão de análise.
 *
 * @param {Number} rodada
 * @param {String} modo `painel` ou `revisao_de_analise`.
 * @returns {String}
 */
export function tituloDaRodada(rodada, modo) {
  if (rodada === 1 && modo === MODO_REVISAO_DE_ANALISE) return TITULO_DA_RODADA_1_REVISAO
  return valorDoMapa(TITULO_DA_RODADA, rodada, 'Rodada ' + rodada)
}

/**
 * O texto honesto do encerramento — vazio para um valor que não conhecemos.
 *
 * @param {String} encerramento
 * @returns {String}
 */
export function textoDoEncerramento(encerramento) {
  return valorDoMapa(TEXTO_DO_ENCERRAMENTO, encerramento, '')
}

function decorarItens(itens) {
  return (itens || []).map(function(item, i) {
    return {
      ...item,
      chave: item.classe + '-' + i,
      cor: valorDoMapa(COR_POR_CLASSE, item.classe, COR_CLASSE_PADRAO),
      rotulo: valorDoMapa(ROTULO_DA_CLASSE, item.classe, item.classe),
    }
  })
}

function decorarQuote(turno) {
  const quote = turno.quote
  if (!quote || !quote.texto_item) return null
  // A atribuição é sempre de quem ESCREVEU o item citado. No fluxo de revisão o
  // endereçamento do turno muda ("sobre a sua análise"), mas o quote da rodada 2
  // continua citando outra área — atribuí-lo ao gestor seria mentira.
  return {
    texto: truncarQuote(quote.texto_item),
    atribuicao: '— ' + (quote.area_origem_label || quote.area_origem || ''),
  }
}

/**
 * O endereçamento em texto — é o que separa debate de pareceres paralelos.
 *
 * Réplica nomeia a área a que responde. No fluxo de revisão, o parecer da
 * rodada 1 nomeia o destinatário real da crítica: o gestor (US-7).
 *
 * @param {Object} turno
 * @param {String} modo
 * @returns {String} Vazio quando o turno não endereça ninguém.
 */
function enderecamentoDoTurno(turno, modo) {
  if (turno.tipo === 'replica') return 'responde a ' + (turno.responde_a_label || '')
  if (turno.tipo === 'parecer' && modo === MODO_REVISAO_DE_ANALISE) return ENDERECAMENTO_REVISAO
  return ''
}

function decorarExpansao(turno, temFala) {
  const servicos = (turno.servicos || []).map(function(s, i) {
    return { ...s, chave: 'servico-' + i }
  })
  const perguntas = (turno.perguntas || []).map(function(p, i) {
    return { chave: 'pergunta-' + i, texto: p }
  })
  const confianca = turno.confianca || ''
  const confiancaPorque = turno.confianca_porque || ''
  return {
    servicos: servicos,
    perguntas: perguntas,
    confianca: confianca,
    confiancaPorque: confiancaPorque,
    // Sem nada além do resumo compacto, o botão de expandir seria uma promessa
    // vazia — não se oferece abrir uma gaveta que está vazia. Com fala, o
    // estruturado inteiro (leitura, itens, frase) mora na gaveta, então ela
    // sempre tem conteúdo.
    temConteudo: Boolean(temFala || servicos.length || perguntas.length || confianca),
  }
}

/**
 * A identidade da área do turno: o id e o NOME DE NEGÓCIO, resolvidos pela mesa.
 *
 * ⚠️ É aqui que o slug parava de virar nome. O turno da rodada 3
 * (`tipo: 'condicao'`) chega do backend com `area: ''` e o ID da área dentro de
 * `label` — `transcricao.py::turno_de_condicao` faz `'label': condicao['area']`.
 * O front confiava no `label` como nome pronto, então a rodada 3 desenhava
 * `juridico_regulatorio` cru e ainda caía no ícone do orquestrador (sem `area`,
 * `iconeDaArea` não tem o que procurar). Um defeito, dois sintomas.
 *
 * A MESA é a única fonte do nome, como já era em `decorarDivergencias` e
 * `decorarOrquestrador` — `constants/Areas.js` guarda só o ícone, de propósito:
 * duplicar o nome criaria duas versões que divergem no primeiro renome.
 *
 * Área que a mesa não conhece mantém o que o turno trouxe — inventar nome seria
 * pior que mostrar o que o backend mandou.
 *
 * @param {Object} turno Turno da transcrição.
 * @param {Object} rotulos Mapa `area → label` da mesa.
 * @returns {Object} `{ area, label }` já resolvidos.
 */
function identidadeDoTurno(turno, rotulos) {
  const area = turno.area || (Object.hasOwn(rotulos, turno.label) ? turno.label : '')
  return { area: area, label: valorDoMapa(rotulos, area, turno.label || area) }
}

/**
 * Achata um turno no que o template renderiza, sem lógica no `<template>`.
 *
 * @param {Object} turno Turno da transcrição.
 * @param {Number} indice Posição no grupo — entra na `:key`, já que a mesma
 *   área pode ter mais de um turno na mesma rodada (uma réplica por conflito).
 * @param {String} modo Modo da deliberação (`painel` por default).
 * @param {Object} [rotulos] Mapa `area → label` da mesa (`rotulosDaMesa`). Sem
 *   ele o turno fica com o rótulo que trouxe — é o comportamento de antes.
 * @returns {Object} Turno decorado.
 */
export function decorarTurno(turno, indice, modo, rotulos = {}) {
  const ehReplica = turno.tipo === 'replica'
  const identidade = identidadeDoTurno(turno, rotulos)
  const corDaPosicao = valorDoMapa(COR_POR_POSICAO, turno.posicao, COR_POSICAO_PADRAO)
  // A fala é o que o especialista DISSE, em primeira pessoa — é ela que o
  // gestor lê primeiro (P10). Os itens estruturados (classes, âncoras, posição)
  // continuam existindo, mas como detalhe da fala, não como o rosto do turno.
  const fala = String(turno.fala || '').trim()
  const temFala = Boolean(fala)
  return {
    ...turno,
    area: identidade.area,
    label: identidade.label,
    chave: turno.tipo + '-' + (identidade.area || identidade.label) + '-' + indice,
    icone: iconeDaArea(identidade.area),
    ehParecer: turno.tipo === 'parecer',
    ehReplica: ehReplica,
    ehCondicao: turno.tipo === 'condicao',
    fala: fala,
    temFala: temFala,
    // Sem fala, o estruturado é o próprio conteúdo do turno e fica no corpo —
    // é assim que a sala sempre foi, e conversa antiga não pode mudar de cara.
    estruturadoInline: !temFala,
    corDaPosicao: corDaPosicao,
    // O mesmo fato da linha acima, já vestido de tag discreta. Derivado, e não
    // um segundo mapa: dois donos do "que cor tem esta posição" divergiriam.
    classeDaPosicao: valorDoMapa(CLASSE_POR_COR, corDaPosicao, CLASSE_DE_TOM_PADRAO),
    enderecamento: enderecamentoDoTurno(turno, modo),
    quote: decorarQuote(turno),
    expansao: decorarExpansao(turno, temFala),
    itens: decorarItens(turno.itens),
  }
}

/**
 * Os cards das áreas que ainda não falaram, na ordem da mesa.
 *
 * Existem só na sala AO VIVO: montados na abertura, somem quando o turno real
 * chega. É o que impede a mesa de aparecer vazia enquanto o painel roda (W1).
 *
 * @param {Object} painel Parcial de `reduzirEventos`.
 * @returns {Array} Cards prontos para o template.
 */
export function montarCardsVivos(painel) {
  if (!painel || !painel.aoVivo) return []
  const comTurno = areasComTurnoPorRodada(painel.turnos)
  const cards = []
  for (const cadeira of painel.mesa || []) {
    const card = cardDaCadeira(cadeira, painel, comTurno)
    if (card) cards.push(card)
  }
  return cards.concat(cardsDasRodadasSeguintes(painel, comTurno))
}

function areasComTurnoPorRodada(turnos) {
  const porRodada = {}
  for (const turno of turnos || []) {
    if (!turno || !turno.area) continue
    const chave = turno.area + '|' + turno.rodada
    porRodada[chave] = true
  }
  return porRodada
}

function jaTemTurno(comTurno, area, rodada) {
  return Object.hasOwn(comTurno, area + '|' + rodada)
}

function cardDaCadeira(cadeira, painel, comTurno) {
  const area = cadeira && cadeira.area
  if (!area || jaTemTurno(comTurno, area, 1)) return null
  const estado = valorDoMapa(painel.estadoPorArea || {}, area, '')
  const fala = falaDe(painel, area, 1)
  // `concluido` sem turno é a janela de milissegundos entre os dois eventos que
  // a conclusão emite — mostrar spinner aí piscaria. Com fala na tela, porém, o
  // card continua: apagá-la para reexibi-la um instante depois é pior.
  if (estado === ESTADO_CONCLUIDO && !fala) return null
  const indisponivel = estado === ESTADO_INDISPONIVEL
  return {
    chave: 'card-' + area + '-1',
    area: area,
    rodada: 1,
    label: cadeira.label || area,
    icone: iconeDaArea(area),
    indisponivel: indisponivel,
    texto: indisponivel ? TEXTO_NAO_RESPONDEU : TEXTO_ANALISANDO,
    motivo: indisponivel ? valorDoMapa(painel.motivoPorArea || {}, area, '') : '',
    fala: indisponivel ? '' : fala,
    falando: falandoAgora(painel, area, 1),
  }
}

/**
 * Cards das rodadas 2 e 3 — existem SÓ enquanto alguém está falando nelas.
 *
 * A mesa não monta cards fantasma para réplicas que talvez não aconteçam (W6);
 * mas, quando uma réplica começa a ser escrita, ela precisa aparecer na hora —
 * é o que faz o gestor ver a conversa acontecendo, e não só o resultado dela.
 *
 * @param {Object} painel
 * @param {Object} comTurno Índice `area|rodada` dos turnos já recebidos.
 * @returns {Array}
 */
function cardsDasRodadasSeguintes(painel, comTurno) {
  const rotulos = rotulosDaMesa(painel.mesa)
  const cards = []
  for (const chave of Object.keys(painel.falas || {})) {
    const fala = painel.falas[chave]
    if (!fala || fala.rodada === 1) continue
    if (jaTemTurno(comTurno, fala.area, fala.rodada)) continue
    cards.push({
      chave: 'card-' + fala.area + '-' + fala.rodada,
      area: fala.area,
      rodada: fala.rodada,
      label: valorDoMapa(rotulos, fala.area, fala.area),
      icone: iconeDaArea(fala.area),
      indisponivel: false,
      texto: TEXTO_ANALISANDO,
      motivo: '',
      fala: fala.texto,
      falando: !fala.fim,
    })
  }
  return cards
}

/**
 * As divergências que sobraram, com os nomes de negócio das áreas.
 *
 * Lista vazia (o caso convergente) devolve `[]` — a seção não renderiza. Uma
 * seção "Divergência em aberto" vazia sugeriria que algo falhou ao carregar.
 *
 * @param {Object} parecer Parecer estruturado da transcrição.
 * @param {Array} mesa Mesa do painel — de onde vêm os rótulos das áreas.
 * @returns {Array}
 */
export function decorarDivergencias(parecer, mesa) {
  if (!parecer || !parecer.divergencias || !parecer.divergencias.length) return []
  const rotulos = rotulosDaMesa(mesa)
  return parecer.divergencias.map(function(divergencia, i) {
    const areas = (divergencia.areas || []).map(function(area) {
      return valorDoMapa(rotulos, area, area)
    })
    return {
      chave: 'divergencia-' + i,
      areas: areas.join(' × '),
      natureza: valorDoMapa(ROTULO_DA_NATUREZA, divergencia.natureza, divergencia.natureza || ''),
      descricao: divergencia.descricao || '',
      // Os dois shapes dizem coisas diferentes e por isso não se fundem num
      // campo só: o determinístico aponta a condição que DERRUBA a restrição
      // ("cai se"); o Orquestrador aponta o que FAZER para resolver o impasse.
      condicao: divergencia.condicao_de_dispensa || '',
      caminho: divergencia.caminho_de_resolucao || '',
    }
  })
}

/**
 * O mapa `area → nome de negócio` da mesa deste painel.
 *
 * Exportada porque virou a fonte ÚNICA do nome de área na sala: as divergências,
 * o parecer do Orquestrador e agora também o turno (`decorarTurno`) leem daqui.
 *
 * @param {Array} mesa Mesa do painel (`mesa_para_ui` do backend).
 * @returns {Object}
 */
export function rotulosDaMesa(mesa) {
  const rotulos = {}
  for (const cadeira of mesa || []) {
    if (cadeira && cadeira.area) rotulos[cadeira.area] = cadeira.label || cadeira.area
  }
  return rotulos
}

/**
 * Os fundamentos de uma área, nos dois shapes de parecer.
 *
 * O Orquestrador escreve `{afirmacao, fonte}`; a derivação determinística só tem
 * a âncora que a área citou (`{tipo, ref}`), sem frase. Não se inventa afirmação
 * para o segundo caso — sobra a fonte, que é o que existe.
 *
 * @param {Object} area Entrada de `parecer.por_area`.
 * @returns {Array}
 */
function fundamentosDaArea(area) {
  const doOrquestrador = area.fundamentos || []
  if (doOrquestrador.length) {
    return doOrquestrador.map(function(fundamento, i) {
      return {
        chave: 'fundamento-' + i,
        afirmacao: (fundamento && fundamento.afirmacao) || '',
        fonte: (fundamento && fundamento.fonte) || '',
      }
    })
  }
  return (area.ancoras || []).map(function(ancora, i) {
    return { chave: 'fundamento-' + i, afirmacao: '', fonte: (ancora && ancora.ref) || '' }
  })
}

function comoLista(itens, prefixo) {
  return (itens || []).map(function(texto, i) {
    return { chave: prefixo + '-' + i, texto: texto }
  })
}

/**
 * O turno de fechamento do Orquestrador Sênior — ou `null` quando não houve um.
 *
 * Existe quando o parecer veio do caminho da consolidação: ou o Orquestrador
 * arbitrou (`posicao_consolidada`), ou tentou e o guardrail o rebaixou
 * (`consolidacao_fallback`). Uma conversa persistida ANTES da W7 tem só o
 * parecer determinístico, sem nenhum dos dois — e nela não se inventa um turno
 * de Orquestrador que nunca aconteceu.
 *
 * @param {Object} painel Transcrição (ao vivo ou reaberta).
 * @returns {Object|null}
 */
export function decorarOrquestrador(painel) {
  const parecer = painel && painel.parecer
  if (!parecer) return null
  const ehFallback = Boolean(parecer.consolidacao_fallback)
  const posicao = parecer.posicao_consolidada || ''
  if (!posicao && !ehFallback) return null

  const rotulos = rotulosDaMesa(painel.mesa)
  return {
    rotulo: ROTULO_ORQUESTRADOR,
    icone: ICONE_ORQUESTRADOR,
    ehFallback: ehFallback,
    // O `consolidacao_motivo` do backend é diagnóstico de execução (qual fonte
    // não existia, quantas tentativas) — não fala com o gestor, e exibi-lo cru
    // trocaria uma informação útil por um log.
    aviso: ehFallback ? TEXTO_CONSOLIDACAO_AUTOMATICA : '',
    explicacao: ehFallback ? EXPLICACAO_CONSOLIDACAO_AUTOMATICA : '',
    posicaoConsolidada: posicao,
    porArea: (parecer.por_area || []).map(function(area) {
      const fundamentos = fundamentosDaArea(area)
      return {
        chave: 'orquestrador-' + (area.area || area.label || ''),
        label: area.label || valorDoMapa(rotulos, area.area, area.area || ''),
        posicao: area.posicao || '',
        fundamentos: fundamentos,
        temFundamentos: fundamentos.length > 0,
      }
    }),
    ...listasDoParecer(parecer),
  }
}

/**
 * As três listas do fim do parecer, nomeadas e também agrupadas em `secoes`.
 *
 * `secoes` referencia as MESMAS listas (não cópias) para o template ter um laço
 * só em vez de três blocos quase iguais; seção vazia não entra, porque um título
 * "Próximos passos" sem passo nenhum leria como falha de carregamento.
 *
 * @param {Object} parecer
 * @returns {Object}
 */
function listasDoParecer(parecer) {
  const proximosPassos = comoLista(parecer.proximos_passos, 'passo')
  const perguntasEmAberto = comoLista(parecer.perguntas_em_aberto, 'pergunta')
  const limitacoes = comoLista(parecer.limitacoes, 'limitacao')
  // ★ Só `limitacoes`. "Próximos passos" e "Perguntas em aberto" saíram daqui
  // no merge com a branch do redesign: elas já são BLOCOS próprios depois do
  // painel (`next-steps` e `open-questions`), no desenho que foi validado, e
  // mantê-las também dentro do orquestrador repetia a mesma lista duas vezes na
  // mesma resposta. As listas nomeadas seguem exportadas — quem some é só a
  // duplicata visual.
  const secoes = [
    { chave: 'limitacoes', titulo: 'Limitações', itens: limitacoes },
  ]
  return {
    proximosPassos: proximosPassos,
    perguntasEmAberto: perguntasEmAberto,
    limitacoes: limitacoes,
    secoes: secoes.filter(function(secao) { return secao.itens.length > 0 }),
  }
}

/**
 * A métrica do painel: quantas áreas, quantas rodadas, quanto tempo, quanto custou.
 *
 * Cada parte só entra quando existe — durante a deliberação não há duração nem
 * custo, e um "0s · R$ 0,00" mentiria sobre um painel que ainda está rodando.
 *
 * ★ O CUSTO é opt-in (`mostrarCusto`), e o default é NÃO mostrar. É informação
 * de operação da AAWZ — quanto a inferência custou —, não do gestor do
 * escritório: para ele é um número sem contexto ao lado de uma resposta que ele
 * não pediu para precificar. Quem decide é o componente, que conhece o usuário;
 * esta função continua pura para seguir testável sem montar Vue.
 *
 * @param {Object} painel
 * @param {Object} [opcoes]
 * @param {Boolean} [opcoes.mostrarCusto=false] Só usuário interno vê o custo.
 * @returns {String}
 */
export function metricaDoPainel(painel, { mostrarCusto = false } = {}) {
  if (!painel) return ''
  const areas = (painel.mesa || []).length
  const partes = [areas + (areas === 1 ? ' área' : ' áreas')]
  if (painel.rodadas) partes.push(painel.rodadas + (painel.rodadas === 1 ? ' rodada' : ' rodadas'))
  if (painel.duracao_s) partes.push(painel.duracao_s + 's')
  if (mostrarCusto && typeof painel.custo_brl === 'number') {
    partes.push(toMoney(painel.custo_brl))
  }
  return partes.join(' · ')
}

/**
 * A mesa em uma linha: quem está e quem conduz.
 *
 * @param {Object} painel
 * @returns {String}
 */
export function condutorDaMesa(painel) {
  if (!painel) return ''
  const lider = (painel.mesa || []).find(function(cadeira) {
    return cadeira.lead
  })
  return lider ? lider.label + ' ' + ROTULO_CONDUZ : ''
}
